import { ANALYSIS_STATUS_LABELS, type Analysis } from '@convometry/contracts';
import { Info, Lightbulb, Sparkles, ThumbsUp } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { formatDateTime } from '../../lib/format.js';
import { ScoreBadge, ScoreBreakdown, IntentBadge, MotiveBadge, EvidenceLink } from '../analytics/score.js';
import { Badge } from '../primitives/badge.js';

export type AnalysisPanelData = Pick<
  Analysis,
  | 'status'
  | 'isProvisional'
  | 'primaryMotive'
  | 'intent'
  | 'summary'
  | 'productInterest'
  | 'objections'
  | 'nextAction'
  | 'qualityScore'
  | 'dimensions'
  | 'evidenceMessageIds'
  | 'evidenceCoverage'
  | 'uncertaintyFlags'
  | 'strengths'
  | 'coaching'
  | 'selfReportedConfidence'
  | 'model'
  | 'promptVersion'
  | 'rubricVersion'
  | 'isDemo'
  | 'error'
  | 'completedAt'
>;

export interface AnalysisPanelProps {
  analysis: AnalysisPanelData | null;
  timeZone: string;
  onEvidenceClick?: (messageId: string) => void;
  evidenceLabel?: (messageId: string) => string;
  /** When set, evidence renders as links (e.g. "#msg-<id>"). */
  evidenceHref?: (messageId: string) => string;
  /** Slot for actions such as "Reanalizar" (the component never performs requests). */
  actions?: React.ReactNode;
  className?: string;
}

const STATUS_HELP: Partial<Record<Analysis['status'], string>> = {
  pending: 'El análisis se ejecuta tras un periodo de inactividad de la conversación.',
  running: 'La IA está evaluando esta conversación.',
  insufficient_evidence: 'No hay un intercambio suficiente (se requiere al menos un mensaje sustantivo de cada lado) o falta contenido evaluable, como audio no transcrito.',
  failed: 'El análisis falló. Las métricas operativas no se ven afectadas.',
  disabled: 'La evaluación con IA está desactivada o no hay credenciales configuradas.',
  budget_limited: 'Se alcanzó el límite diario de análisis de este negocio.',
  stale: 'La conversación cambió después de este análisis.',
};

function Section({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="grid gap-1.5">
      <h4 className="flex items-center gap-1.5 text-helper font-semibold uppercase tracking-wide text-text-secondary [&_svg]:size-4">
        {icon}
        {title}
      </h4>
      {children}
    </section>
  );
}

export function AnalysisPanel({ analysis, timeZone, onEvidenceClick, evidenceLabel, evidenceHref, actions, className }: AnalysisPanelProps) {
  if (!analysis) {
    return (
      <div className={cn('grid gap-3', className)}>
        <ScoreBadge status="pending" />
        <p className="text-body text-text-secondary">Esta conversación aún no tiene análisis.</p>
        {actions}
      </div>
    );
  }
  const a = analysis;
  const completed = a.status === 'completed';
  return (
    <div className={cn('grid gap-5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <ScoreBadge status={a.status} score={a.qualityScore} provisional={a.isProvisional} />
        {a.isProvisional ? <Badge tone="info">Provisional · episodio abierto</Badge> : null}
        {a.isDemo ? <Badge tone="warning">Demo · análisis sintético</Badge> : null}
        {actions ? <div className="ml-auto">{actions}</div> : null}
      </div>
      {!completed ? (
        <p className="flex gap-2 text-body text-text-secondary">
          <Info aria-hidden className="mt-0.5 size-4 shrink-0" />
          <span>
            <span className="font-medium text-text-primary">{ANALYSIS_STATUS_LABELS[a.status]}.</span> {STATUS_HELP[a.status]}
            {a.error && a.status === 'failed' ? <span className="block text-helper text-text-muted">Código: {a.error}</span> : null}
          </span>
        </p>
      ) : null}

      {a.summary || a.primaryMotive || a.intent ? (
        <Section title="Lead" icon={<Sparkles aria-hidden />}>
          <div className="flex flex-wrap gap-2">
            <MotiveBadge motive={a.primaryMotive} />
            <IntentBadge intent={a.intent} />
          </div>
          {a.summary ? <p className="text-body text-text-primary">{a.summary}</p> : null}
          <dl className="grid gap-1 text-body sm:grid-cols-[max-content_1fr] sm:gap-x-4">
            {a.productInterest ? (
              <>
                <dt className="text-text-secondary">Interés</dt>
                <dd className="text-text-primary">{a.productInterest}</dd>
              </>
            ) : null}
            <dt className="text-text-secondary">Objeciones</dt>
            <dd className="text-text-primary">{a.objections.length ? a.objections.join(' · ') : 'Ninguna identificada'}</dd>
            <dt className="text-text-secondary">Siguiente paso acordado</dt>
            <dd className="text-text-primary">{a.nextAction ?? 'No se acordó'}</dd>
          </dl>
        </Section>
      ) : null}

      {completed || a.dimensions.length > 0 ? (
        <Section title="Calidad de la conversación (asesor)">
          <ScoreBreakdown dimensions={a.dimensions} onEvidenceClick={onEvidenceClick} evidenceLabel={evidenceLabel} evidenceHref={evidenceHref} />
        </Section>
      ) : null}

      {a.strengths.length > 0 ? (
        <Section title="Fortalezas" icon={<ThumbsUp aria-hidden />}>
          <ul className="list-disc space-y-1 pl-5 text-body text-text-primary">
            {a.strengths.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </Section>
      ) : null}
      {a.coaching.length > 0 ? (
        <Section title="Sugerencias de mejora" icon={<Lightbulb aria-hidden />}>
          <ul className="list-disc space-y-1 pl-5 text-body text-text-primary">
            {a.coaching.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      {a.evidenceMessageIds.length > 0 ? (
        <Section title="Evidencia">
          <div className="flex flex-wrap gap-1.5">
            {a.evidenceMessageIds.map((id) => (
              <EvidenceLink key={id} messageId={id} label={evidenceLabel?.(id)} href={evidenceHref?.(id)} onSelect={onEvidenceClick} />
            ))}
          </div>
        </Section>
      ) : null}

      <div className="grid gap-1 border-t border-border-subtle pt-3 text-helper text-text-secondary">
        {a.evidenceCoverage ? (
          <p className="cm-tabular">
            Analizados {a.evidenceCoverage.analyzedMessages} de {a.evidenceCoverage.totalMessages} mensajes
            {a.evidenceCoverage.truncated ? ' · contexto recortado' : ''}
            {a.evidenceCoverage.unavailableMedia > 0 ? ` · ${a.evidenceCoverage.unavailableMedia} audios no disponibles` : ''}
          </p>
        ) : null}
        {a.uncertaintyFlags.length > 0 ? <p>Incertidumbre: {a.uncertaintyFlags.join(', ')}</p> : null}
        {a.selfReportedConfidence !== null ? (
          <p className="cm-tabular">
            Confianza autorreportada por la IA: {Math.round(a.selfReportedConfidence * 100)} % (no es una probabilidad calibrada)
          </p>
        ) : null}
        <p className="cm-tabular">
          {a.model ?? 'modelo —'} · {a.promptVersion} · {a.rubricVersion}
          {a.completedAt ? ` · ${formatDateTime(a.completedAt, timeZone)}` : ''}
        </p>
      </div>
    </div>
  );
}
